import { useEffect, useState } from "react";

export interface CountdownState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
  totalMs: number;
}

const ZERO: CountdownState = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  isPast: true,
  totalMs: 0,
};

function calc(targetMs: number, now: number): CountdownState {
  const diff = targetMs - now;
  if (diff <= 0) return { ...ZERO, isPast: true };
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff / 3_600_000) % 24),
    minutes: Math.floor((diff / 60_000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    isPast: false,
    totalMs: diff,
  };
}

/**
 * Conta regressiva até `target` (Date ou ISO string).
 * - Atualiza a cada 1s
 * - Pausa quando aba fica oculta (economiza bateria + evita drift acumulado)
 * - Resync ao voltar pra visible
 */
export function useCountdown(target: string | Date): CountdownState {
  const targetMs =
    typeof target === "string"
      ? new Date(target).getTime()
      : target.getTime();

  const [state, setState] = useState<CountdownState>(() =>
    typeof window === "undefined" ? ZERO : calc(targetMs, Date.now())
  );

  useEffect(() => {
    let intervalId: number | null = null;

    const tick = () => setState(calc(targetMs, Date.now()));

    const start = () => {
      tick();
      if (intervalId !== null) return;
      intervalId = window.setInterval(tick, 1000);
    };

    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else start();
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [targetMs]);

  return state;
}
